import { useState, useEffect } from 'react';
import { apiService } from '../lib/api-functions/api-service';

export interface EntityReference {
  source_id?: string;
  source_type?: string;
  target_id?: string;
  target_type?: string;
  relationship_context?: string;
}

export interface EntityRelationships {
  referenced_by: EntityReference[];
  references: EntityReference[];
  referenced_by_count: number;
  references_count: number;
}

/**
 * Hook to fetch all relationships for an entity (both incoming and outgoing)
 * @param entityId - The ID of the entity
 * @param entityType - The type of the entity (glossary, document, blueprint, text, etc.)
 * @param gameId - The game ID (optional, will use selected game if not provided)
 */
export function useEntityRelationships(
  entityId: string | undefined,
  entityType: string | undefined,
  gameId?: string
) {
  const [relationships, setRelationships] = useState<EntityRelationships | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip fetching for new items (not yet saved) or missing data
    if (!entityId || !entityType || entityId === 'new') {
      setRelationships(null);
      return;
    }

    const fetchRelationships = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await apiService.getEntityRelationships(entityId, entityType, gameId);
        
        if (result.success && result.data) {
          setRelationships(result.data);
        } else {
          setError(result.error || 'Failed to fetch relationships');
        }
      } catch (err) {
        console.error('Error fetching entity relationships:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchRelationships();
  }, [entityId, entityType, gameId]);

  return { relationships, loading, error };
}

/**
 * Hook to fetch entities that reference a target entity
 * @param targetId - The ID of the target entity
 * @param targetType - The type of the target entity
 * @param gameId - The game ID (optional, will use selected game if not provided)
 */
export function useEntityReferencedBy(
  targetId: string | undefined,
  targetType: string | undefined,
  gameId?: string
) {
  const [referencedBy, setReferencedBy] = useState<EntityReference[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip fetching for new items (not yet saved) or missing data
    if (!targetId || !targetType || targetId === 'new') {
      setReferencedBy([]);
      setCount(0);
      return;
    }

    const fetchReferencedBy = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await apiService.getReferencesToEntity(targetId, targetType, gameId);
        
        if (result.success && result.data) {
          setReferencedBy(result.data.referenced_by || []);
          setCount(result.data.count || 0);
        } else {
          setError(result.error || 'Failed to fetch references');
        }
      } catch (err) {
        console.error('Error fetching entity references:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchReferencedBy();
  }, [targetId, targetType, gameId]);

  return { referencedBy, count, loading, error };
}

/**
 * Hook to fetch entities that a source entity references
 * @param sourceId - The ID of the source entity
 * @param sourceType - The type of the source entity
 * @param gameId - The game ID (optional, will use selected game if not provided)
 */
export function useEntityReferences(
  sourceId: string | undefined,
  sourceType: string | undefined,
  gameId?: string
) {
  const [references, setReferences] = useState<EntityReference[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip fetching for new items (not yet saved) or missing data
    if (!sourceId || !sourceType || sourceId === 'new') {
      setReferences([]);
      setCount(0);
      return;
    }

    const fetchReferences = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await apiService.getReferencesFromEntity(sourceId, sourceType, gameId);
        
        if (result.success && result.data) {
          setReferences(result.data.references || []);
          setCount(result.data.count || 0);
        } else {
          setError(result.error || 'Failed to fetch references');
        }
      } catch (err) {
        console.error('Error fetching entity references:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchReferences();
  }, [sourceId, sourceType, gameId]);

  return { references, count, loading, error };
}

